import { ServiceBase } from "@app/shared";
import { SortDirections } from "../../shared/waffles";
import { LocationConfidenceScoresLabelEnum, NetworkAssessmentAnalysisType, ProviderNetworkType } from "../enumerators";
import { ServicingProviderResult } from "../results/servicing-provider";
import NextClosestProviderResult from "../results/servicing-provider/nextClosestProviderResult";
import { NcpEnum } from "./types";
import { ClaimsActivityLineOfBusinessTypeEnum, ClaimsActivityVolumeLabelEnum } from "@app/projects/network-assessment/pci-dashboard/claims-activity";
import { EffectivenessEfficiencyValueEnum, MotiveScoreValueEnum, NewPatientClaimsEnum } from "@app/projects/servicing-provider";

export default class NextClosestProviderService extends ServiceBase {
  public async requeueNextClosestProvider(projectId: number): Promise<void> {
    return await this.makePost(`nextClosestProvider/${projectId}/resubmit`);
  }

  /**
   * Retrieves the servicing providers for a given project, region, run-configuration, specialty-group-code & provider-network-type
   * @param projectId
   * @param regionSetId
   * @param runConfigurationId
   * @param specialtyGroupCode
   * @param networkAssessmentAnalysisType The type of analsysis for which to retreive results.
   * @param skip
   * @param take
   */
  public async nextClosestNetworkProviders(
    projectId: number,
    regionSetId: number,
    runConfigurationId: number,
    specialtyGroupCode: string,
    networkAssessmentAnalysisType: NetworkAssessmentAnalysisType,
    skip?: number,
    take?: number,
    sortedColumn?: string,
    sortDirection?: SortDirections,
    columnSubKey?: string,
    searchValue?: string,
    selectedEffectivenessValues?: EffectivenessEfficiencyValueEnum[],
    selectedEfficiencyValues?: EffectivenessEfficiencyValueEnum[],
    selectedVolumeLabels?: ClaimsActivityVolumeLabelEnum[],
    selectedLineOfBusiness?: ClaimsActivityLineOfBusinessTypeEnum,
    selectedLocationConfidenceValues?: LocationConfidenceScoresLabelEnum[],
    selectedMotiveAppropriatenessValues?: MotiveScoreValueEnum[],
    selectedNewPatientClaimsValues?: NewPatientClaimsEnum[],
  ): Promise<ServicingProviderResult> {
    // Return early if we don't have any of the good stuff
    if (!projectId || !regionSetId || !runConfigurationId || !specialtyGroupCode || !networkAssessmentAnalysisType) {
      return null;
    }

    if (!selectedVolumeLabels) {
      selectedVolumeLabels = [];
    }

    return await this.makePost<NextClosestProviderResult>(
      `nextClosestProvider/${projectId}/runconfigurations/${runConfigurationId}/regionsets/${regionSetId}/network`,
      {
        columnSubKey,
        networkAssessmentAnalysisType,
        searchValue,
        selectedEffectivenessValues,
        selectedEfficiencyValues,
        selectedLineOfBusiness,
        selectedLocationConfidenceValues,
        selectedMotiveAppropriatenessValues,
        selectedNewPatientClaimsValues,
        selectedVolumeLabels,
        skip,
        sortDirection,
        sortedColumn,
        specialtyGroupCode,
        take,
      }
    );
  }

  /**
   * Retrieves the next closest prospect or market providers for a given project, region, run-configuration, specialty-group-code & provider-network-type
   */
  public async getNextClosestImpactProviders(
    projectId: number,
    regionSetId: number,
    runConfigurationId: number,
    specialtyGroupCode: string,
    networkAssessmentAnalysisType: NetworkAssessmentAnalysisType,
    providerType: ProviderNetworkType,
    skip?: number,
    take?: number,
    selectedColumn?: string,
    sortDirection?: SortDirections,
    columnSubKey?: string,
    ncpFilterValue?: NcpEnum,
    excludedProviders?: boolean,
    searchValue?: string,
    selectedEffectivenessValues?: EffectivenessEfficiencyValueEnum[],
    selectedEfficiencyValues?: EffectivenessEfficiencyValueEnum[],
    selectedVolumeLabels?: ClaimsActivityVolumeLabelEnum[],
    selectedLineOfBusiness?: ClaimsActivityLineOfBusinessTypeEnum,
    selectedLocationConfidenceValues?: LocationConfidenceScoresLabelEnum[],
    selectedMotiveAppropriatenessValues?: MotiveScoreValueEnum[],
  ): Promise<NextClosestProviderResult> {
    // Return early if we don't have any of the good stuff
    if (!projectId || !regionSetId || !runConfigurationId || !specialtyGroupCode || !networkAssessmentAnalysisType) {
      return null;
    }

    return await this.makePost<NextClosestProviderResult>(
      `nextClosestProvider/${projectId}/runconfigurations/${runConfigurationId}/regionsets/${regionSetId}/specialtyGroupCodes/${encodeURIComponent(specialtyGroupCode)}/networkAssessmentAnalysisTypes/${networkAssessmentAnalysisType}/${providerType}`,
      {
        columnSubKey,
        excludedProviders,
        ncpFilterValue,
        searchValue,
        selectedColumn,
        selectedEffectivenessValues,
        selectedEfficiencyValues,
        selectedLineOfBusiness,
        selectedLocationConfidenceValues,
        selectedMotiveAppropriatenessValues,
        selectedVolumeLabels,
        skip,
        sortDirection,
        take,
      });
  }
}